import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const FirmOverview = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const stats = [
    {
      number: '500+',
      label: currentLanguage === 'en' ? 'Satisfied Clients' : 'Wateja Waliordhishwa',
      icon: 'Users'
    },
    {
      number: '15+',
      label: currentLanguage === 'en' ? 'Years Experience' : 'Miaka ya Uzoefu',
      icon: 'Calendar'
    },
    {
      number: '98%',
      label: currentLanguage === 'en' ? 'Success Rate' : 'Kiwango cha Mafanikio',
      icon: 'TrendingUp'
    },
    {
      number: '24/7',
      label: currentLanguage === 'en' ? 'Client Support' : 'Msaada wa Wateja',
      icon: 'Clock'
    }
  ];

  const values = [
    {
      title: currentLanguage === 'en' ? 'Client-Centered Approach' : 'Mbinu Inayolenga Mteja',
      description: currentLanguage === 'en' ?'We prioritize understanding your unique needs and crafting personalized legal strategies that align with your goals.' :'Tunaweka kipaumbele kuelewa mahitaji yako ya kipekee na kuunda mikakati ya kisheria ya kibinafsi inayolingana na malengo yako.',
      icon: 'Heart'
    },
    {
      title: currentLanguage === 'en' ? 'Proven Expertise' : 'Utaalamu Uliothibitishwa',
      description: currentLanguage === 'en' ?'Our team combines deep legal knowledge with practical experience to deliver results that exceed expectations.' :'Timu yetu inachanganya maarifa ya kina ya kisheria na uzoefu wa vitendo kutoa matokeo yanayozidi matarajio.',
      icon: 'Award'
    },
    {
      title: currentLanguage === 'en' ? 'Transparent Communication' : 'Mawasiliano ya Uwazi',
      description: currentLanguage === 'en' ?'We maintain open, honest communication throughout your legal journey, keeping you informed at every step.' :'Tunadumisha mawasiliano ya uwazi na uaminifu katika safari yako ya kisheria, tukikufahamisha kila hatua.',
      icon: 'MessageSquare'
    }
  ];

  const content = {
    en: {
      sectionTitle: "Why Choose Peter & Partners?",
      sectionSubtitle: "Your Trusted Legal Partners in Kenya",
      mainDescription: `At Peter & Partners Advocates LLP, we understand that legal challenges can be overwhelming. That's why we've built our practice around three core principles: expertise, integrity, and client satisfaction.

Our experienced team of legal professionals has successfully handled hundreds of cases across various practice areas, from complex corporate transactions to sensitive family matters. We don't just provide legal advice – we become your strategic partners, working tirelessly to protect your interests and achieve your objectives.What sets us apart is our commitment to making legal services accessible and understandable. We take the time to explain complex legal concepts in plain language, ensuring you're fully informed about your options and the potential outcomes of your case.`,
      learnMoreButton: "Learn More About Us",
      contactButton: "Schedule Consultation"
    },
    sw: {
      sectionTitle: "Kwa Nini Uchague Peter & Partners?",
      sectionSubtitle: "Washirika Wako wa Kuaminika wa Kisheria Kenya",
      mainDescription: `Katika Peter & Partners Advocates LLP, tunaelewa kwamba changamoto za kisheria zinaweza kuwa za kushangaza. Ndiyo maana tumejenga mazoezi yetu kuzunguka kanuni tatu za msingi: utaalamu, uongozi, na kuridhisha wateja.

Timu yetu ya wataalamu wa kisheria wenye uzoefu imeshughulikia kwa mafanikio mamia ya kesi katika maeneo mbalimbali ya mazoezi, kutoka miamala ngumu ya makampuni hadi mambo nyeti ya kifamilia. Hatutoi tu ushauri wa kisheria - tunakuwa washirika wako wa kimkakati, tukifanya kazi bila kuchoka kulinda maslahi yako na kufikia malengo yako.

Kile kinachotufanya tofauti ni kujitolea kwetu kufanya huduma za kisheria zipatikane na zieleweke. Tunachukua muda kuelezea dhana ngumu za kisheria kwa lugha rahisi, kuhakikisha unafahamishwa kikamilifu kuhusu chaguo zako na matokeo yanayowezekana ya kesi yako.`,
      learnMoreButton: "Jifunze Zaidi Kuhusu Sisi",
      contactButton: "Panga Mazungumzo"
    }
  };

  const currentContent = content[currentLanguage];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-surface/5 to-primary/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full">
              <Icon name="Star" size={20} className="text-accent" />
              <span className="text-sm font-medium text-accent">
                {currentLanguage === 'en' ? 'Excellence in Legal Practice' : 'Ubora katika Mazoezi ya Kisheria'}
              </span>
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-text-primary mb-6">
            {currentContent.sectionTitle}
          </h2>
          
          <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            {currentContent.sectionSubtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          {/* Content Column */}
          <div className="space-y-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-text-secondary leading-relaxed whitespace-pre-line">
                {currentContent.mainDescription}
              </p>
            </div>

            {/* Values */}
            <div className="space-y-6">
              {values.map((value, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg flex-shrink-0">
                    <Icon name={value.icon} size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-text-primary mb-2">
                      {value.title}
                    </h3>
                    <p className="text-text-secondary leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/about-us">
                <Button
                  variant="primary"
                  size="lg"
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  {currentContent.learnMoreButton}
                </Button>
              </Link>

              <Link to="/contact-us">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Calendar"
                  iconPosition="left"
                >
                  {currentContent.contactButton}
                </Button>
              </Link>
            </div>
          </div>

          {/* Image Column */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl shadow-lg overflow-hidden">
              <Image
                src="https://images.pexels.com/photos/5668858/pexels-photo-5668858.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Peter & Partners law firm office interior"
                className="w-full h-[500px] object-cover"
              />
              
              {/* Stats Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <div className="grid grid-cols-2 gap-4">
                  {stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="flex items-center justify-center mb-2">
                        <Icon name={stat.icon} size={20} className="text-white mr-2" />
                        <span className="text-2xl font-heading font-bold text-white">
                          {stat.number}
                        </span>
                      </div>
                      <p className="text-sm text-white/90">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-primary/20 rounded-full blur-xl"></div>
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-accent/20 rounded-full blur-xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FirmOverview;